import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import useInactivityTimeout from './hooks/useInactivityTimeout';

// Importar estilos responsive
import './styles/responsive.css';

// Componentes
import Login from './components/Auth/Login';
import PrivateRoute from './components/Auth/PrivateRoute';
import ResponsiveLayout from './components/Layout/ResponsiveLayout'; // ← RESPONSIVE LAYOUT
import Users from './components/Users/Users';
import PaymentList from './components/Payments/PaymentList';
import ExpenseList from './components/Expenses/ExpenseList';
import PeriodList from './components/Periods/PeriodList';
import ParameterList from './components/Parameters/ParameterList';
import BulkUpload from './components/BulkUpload/BulkUpload';
import DebtList from './components/Debts/DebtList';
import AccountStatement from './components/Reports/AccountStatement';
import ImprovedDashboard from './components/Dashboard/ImprovedDashboard';
import OwnerDashboard from './components/OwnerPortal/OwnerDashboard';
import OwnerPayments from './components/OwnerPortal/OwnerPayments';
import OwnerStatement from './components/OwnerPortal/OwnerStatement';
import OwnerExpenses from './components/OwnerPortal/OwnerExpenses';
import BankAccountList from './components/Admin/BankAccountList'; // ← BANK ACCOUNTS
import BankList from './components/Admin/BankList'; // ← BANKS ADMIN
import OwnerBankInfo from './components/OwnerPortal/OwnerBankInfo'; // ← BANK INFO
import Notifications from './components/Notifications/Notifications';
import WhatsAppNotifications from './components/Notifications/WhatsAppNotifications'; // ← WHATSAPP
import BackupManagement from './components/Backup/BackupManagement'; // ← BACKUP
// Comprobantes de pago
import AdminReceiptVerification from './components/Admin/AdminReceiptVerification';
import OwnerPaymentUpload from './components/OwnerPortal/OwnerPaymentUpload';
import Communities from './components/Communities/Communities'; // ← COMMUNITIES
import BankReconciliation from './components/Admin/BankReconciliation';
import BankAccounts from './components/Admin/BankAccounts';
import ServiceProviderList from './components/ServiceProviders/ServiceProviderList';

// Crear tema personalizado
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

// Componente wrapper que usa el hook de inactividad
const AppContent = () => {
  // Activar detección de inactividad (15 minutos)
  useInactivityTimeout();

  return (
    <Routes>
      {/* Ruta pública */}
      <Route path="/login" element={<Login />} />

      {/* Rutas protegidas con ResponsiveLayout */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <ResponsiveLayout />
          </PrivateRoute>
        }
      >
        {/* Redirección inicial */}
        <Route index element={<Navigate to="/dashboard" replace />} />

        {/* Rutas de administrador */}
        <Route path="dashboard" element={<ImprovedDashboard />} />
        <Route path="users" element={<Users />} />
        <Route path="payments" element={<PaymentList />} />
        <Route path="expenses" element={<ExpenseList />} />
        <Route path="periods" element={<PeriodList />} />
        <Route path="parameters" element={<ParameterList />} />
        <Route path="bulk-upload" element={<BulkUpload />} />
        <Route path="debts" element={<DebtList />} />
        <Route path="account-statement" element={<AccountStatement />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="whatsapp" element={<WhatsAppNotifications />} />
        <Route path="backup" element={<BackupManagement />} />
        <Route path="/admin/receipts" element={<AdminReceiptVerification />} />
        <Route path="communities" element={<Communities />} />
        <Route path="bank-accounts" element={<BankAccountList />} />
        <Route path="banks" element={<BankList />} />

        {/* Portal de Propietarios */}
        <Route path="owner/dashboard" element={<OwnerDashboard />} />
        <Route path="owner/payments" element={<OwnerPayments />} />
        <Route path="owner/statement" element={<OwnerStatement />} />
        <Route path="owner/expenses" element={<OwnerExpenses />} />
        <Route path="/owner/upload-payment" element={<OwnerPaymentUpload />} />
        <Route path="owner/bank-info" element={<OwnerBankInfo />} />
        <Route path="/admin/bank-reconciliation" element={<BankReconciliation />} />
        <Route path="/admin/bank-accounts" element={<BankAccounts />} />
        <Route path="service-providers" element={<ServiceProviderList />} />
      </Route>

      {/* Redirección por defecto */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
